import React, { useEffect, useState } from "react";
import {
  getSystemState,
  subscribeSystemState,
} from "./SystemState";
import {
  getBootState,
  subscribeBootState,
} from "./BootManager";
import {
  getRouteState,
  subscribeRouteState,
} from "./RouteManager";
import {
  getMediaSession,
  subscribeMediaSession,
} from "./MediaSessionManager";
import {
  getOverlayState,
  subscribeOverlayState,
} from "./OverlayManager";
import {
  closeSystemStatus,
  getSystemStatusState,
  subscribeSystemStatus,
} from "./SystemStatusManager";
import {
  handleBack,
  registerBackHandler,
  unregisterBackHandler,
} from "./BackManager";

// NapflixOS System Status v12
// Read-only OS runtime panel. No playback, navigation, or focus ownership changes.
const boolText = (value) => (value ? "Yes" : "No");
const valueText = (value) =>
  value === null || value === undefined || value === "" ? "—" : String(value);

const StatusRow = ({ label, value }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      gap: 18,
      padding: "7px 0",
      borderBottom: "1px solid rgba(255,255,255,0.07)",
    }}
  >
    <span style={{ color: "rgba(255,255,255,0.48)" }}>{label}</span>
    <span
      style={{
        color: "rgba(255,255,255,0.9)",
        textAlign: "right",
        maxWidth: 240,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {valueText(value)}
    </span>
  </div>
);

const StatusSection = ({ title, children }) => (
  <section>
    <div
      style={{
        marginBottom: 8,
        fontSize: 10,
        letterSpacing: 2,
        textTransform: "uppercase",
        color: "rgba(229,199,93,0.9)",
      }}
    >
      {title}
    </div>
    {children}
  </section>
);

export default function SystemStatusOverlay() {
  const [panelState, setPanelState] = useState(getSystemStatusState());
  const [systemState, setSystemState] = useState(getSystemState());
  const [bootState, setBootState] = useState(getBootState());
  const [routeState, setRouteState] = useState(getRouteState());
  const [mediaSession, setMediaSession] = useState(getMediaSession());
  const [overlayState, setOverlayState] = useState(getOverlayState());

  useEffect(() => {
    setPanelState(getSystemStatusState());
    return subscribeSystemStatus(setPanelState);
  }, []);

  useEffect(() => {
    registerBackHandler({
      id: "system-status",
      priority: 95,
      canHandle: () => getSystemStatusState().open,
      handle: () => {
        closeSystemStatus();
        return true;
      },
      label: "System Status",
    });

    return () => unregisterBackHandler("system-status");
  }, []);

  useEffect(() => {
    setSystemState(getSystemState());
    const unsubSystem = subscribeSystemState(setSystemState);
    const unsubBoot = subscribeBootState(setBootState);
    const unsubRoute = subscribeRouteState(setRouteState);
    const unsubMedia = subscribeMediaSession(setMediaSession);
    const unsubOverlay = subscribeOverlayState(setOverlayState);

    return () => {
      unsubSystem();
      unsubBoot();
      unsubRoute();
      unsubMedia();
      unsubOverlay();
    };
  }, []);

  useEffect(() => {
    const onKey = (event) => {
      if (!getSystemStatusState().open) return;
      if (event.key !== "Escape" && event.key !== "Backspace") return;

      event.preventDefault();
      event.stopImmediatePropagation?.() || event.stopPropagation();
      handleBack("keyboard");
    };

    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);

  if (!panelState.open) return null;

  const mediaState = mediaSession.buffering
    ? "Buffering"
    : mediaSession.playing
      ? "Playing"
      : mediaSession.paused
        ? "Paused"
        : "Inactive";

  return (
    <div
      aria-label="Napflix system status"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) event.preventDefault();
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2147483260,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "auto",
        background:
          "radial-gradient(circle at center, rgba(28, 38, 60, 0.46), rgba(0,0,0,0.66) 72%)",
      }}
    >
      <div
        style={{
          width: "min(760px, calc(100vw - 56px))",
          maxHeight: "min(720px, calc(100vh - 56px))",
          overflow: "auto",
          borderRadius: 18,
          border: "1px solid rgba(255,255,255,0.18)",
          background: "rgba(8, 12, 22, 0.9)",
          boxShadow:
            "0 30px 90px rgba(0,0,0,0.66), inset 0 1px 0 rgba(255,255,255,0.12)",
          backdropFilter: "blur(20px) saturate(1.25)",
          color: "rgba(255,255,255,0.9)",
          fontFamily: "'Outfit', sans-serif",
          padding: 24,
        }}
      >
        <div
          style={{
            fontSize: 12,
            letterSpacing: 2.6,
            textTransform: "uppercase",
            color: "rgba(229,199,93,0.95)",
            marginBottom: 18,
          }}
        >
          System Status
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 24,
            fontSize: 13,
          }}
        >
          <StatusSection title="Runtime">
            <StatusRow label="NapflixOS active" value={boolText(systemState.isNapflixOS)} />
            <StatusRow label="Idle" value={boolText(systemState.isIdle)} />
            <StatusRow label="Fullscreen" value={boolText(systemState.isFullscreen)} />
            <StatusRow label="Network online" value={boolText(systemState.networkOnline)} />
          </StatusSection>

          <StatusSection title="Boot">
            <StatusRow label="Phase" value={bootState.phase} />
            <StatusRow label="Plex ready" value={boolText(bootState.plexReady)} />
            <StatusRow label="PJTV ready" value={boolText(bootState.pjtvReady)} />
            <StatusRow label="Home cache ready" value={boolText(bootState.homeCacheReady)} />
            <StatusRow label="Errors" value={bootState.errors?.length || 0} />
          </StatusSection>

          <StatusSection title="Route">
            <StatusRow label="Current page" value={routeState.currentPage} />
            <StatusRow label="Previous page" value={routeState.previousPage} />
          </StatusSection>

          <StatusSection title="Media">
            <StatusRow label="Title" value={mediaSession.title} />
            <StatusRow label="Source" value={mediaSession.source} />
            <StatusRow label="State" value={mediaState} />
          </StatusSection>

          <StatusSection title="Overlays">
            <StatusRow label="Top overlay" value={overlayState.topOverlay?.type} />
            <StatusRow label="Modal active" value={boolText(overlayState.hasModalOverlay)} />
          </StatusSection>
        </div>
      </div>
    </div>
  );
}
